import { LocaleSettingsResult, TimeOnly, TimeSpan } from "@repo/rq-api-public";
import { format, type Locale } from "date-fns";
import * as dateFnsLocales from "date-fns/locale";

export function getDateFnsLocale(culture: string): Locale {
  const locales = dateFnsLocales as unknown as Record<string, Locale>;
  const [language, region] = culture.replace("_", "-").split("-");
  const regionalLocale =
    (region ? locales[`${language}${region.toUpperCase()}`] : undefined) ??
    locales[language ?? ""] ??
    dateFnsLocales.enUS;

  return {
    ...dateFnsLocales.enUS,
    options: regionalLocale.options ?? dateFnsLocales.enUS.options,
  };
}

export class DynamicDateFormatter {
  constructor(private settings: LocaleSettingsResult) {}

  get locale() {
    return getDateFnsLocale(this.settings.culture ?? "en-US");
  }

  formatDateOnly(date: Date): string {
    return format(date, this.settings.dateFormat.pattern ?? "M/d/yyyy");
  }

  formatTimeOnly(date: Date): string {
    return format(
      date,
      this.settings.timeFormat.use12Hour ? "h:mm a" : "HH:mm",
    );
  }

  formatClockTime(time: TimeOnly | undefined): string {
    if (!time || time.hour > 24 || time.minute > 59) return "Invalid time";
    return this.formatTimeOnly(new Date(0, 0, 1, time.hour % 24, time.minute));
  }

  formatClockRange(
    startTime: TimeOnly | undefined,
    duration: TimeSpan | undefined,
  ): string {
    if (duration?.hours === 24 && duration?.minutes === 0) return "Full Day";
    return `${this.formatClockTime(startTime)} - ${this.formatClockTime(
      startTime && duration ? this.getEndTime(startTime, duration) : undefined,
    )}`;
  }

  formatDuration(duration: TimeSpan | undefined): string {
    if (!duration) return "Invalid duration";
    const hours = duration.hours ?? 0;
    const minutes = duration.minutes ?? 0;
    const hoursPart = hours ? `${hours} hour${hours === 1 ? "" : "s"}` : "";
    const minutesPart = minutes ? `${minutes} min` : "";
    return [hoursPart, minutesPart].filter(Boolean).join(" ") || "0 min";
  }

  private getEndTime(startTime: TimeOnly, duration: TimeSpan): TimeOnly {
    const minutes = startTime.minute + (duration.minutes ?? 0);
    const hours =
      startTime.hour + (duration.hours ?? 0) + Math.floor(minutes / 60);
    return new TimeOnly({ hour: hours % 24, minute: minutes % 60 });
  }
}
